import { useEffect, useState } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/combobox';
import { cn } from '@/lib/utils';
import {
  HedgeBookFilters,
  INSTRUMENT_OPTIONS,
  isAnyHedgeBookFilterActive,
  METAL_OPTIONS,
  STATUS_FILTER_LABELS,
  STATUS_OPTIONS,
  type HedgeBookInstrument,
} from '../lib/hedge-book-filters';
import { TICKER_LABEL } from '../lib/risk-summary-derivation';

interface HedgeBookFilterBarProps {
  filters: HedgeBookFilters;
  update: (patch: Partial<HedgeBookFilters>) => void;
  clearAll: () => void;
  total: number | undefined;
  onNewRequest: () => void;
}

const STATUS_SELECT_OPTIONS = STATUS_OPTIONS.map((value) => ({
  value,
  label: STATUS_FILTER_LABELS[value],
}));

const ASSET_SELECT_OPTIONS = METAL_OPTIONS.map((value) => ({
  value,
  label: TICKER_LABEL[value] ?? value,
}));

export function HedgeBookFilterBar({
  filters,
  update,
  clearAll,
  total,
  onNewRequest,
}: Readonly<HedgeBookFilterBarProps>) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <InstrumentToggle
        value={filters.instrument}
        onChange={(instrument) => {
          // The asset filter only applies to metals, so it resets on switch.
          update({ instrument, metal: undefined });
        }}
      />
      <SearchField
        current={filters.search}
        onSubmit={(value) => {
          update({ search: value });
        }}
      />
      <FilterSelects filters={filters} update={update} />
      <BarTrailing
        total={total}
        anyActive={isAnyHedgeBookFilterActive(filters)}
        clearAll={clearAll}
        onNewRequest={onNewRequest}
      />
    </div>
  );
}

function FilterSelects({
  filters,
  update,
}: Readonly<Pick<HedgeBookFilterBarProps, 'filters' | 'update'>>) {
  return (
    <>
      <SelectField
        label="Status"
        value={filters.status}
        allLabel="Active"
        options={STATUS_SELECT_OPTIONS}
        onChange={(value) => {
          update({ status: value });
        }}
      />
      {filters.instrument === 'metals' && (
        <SelectField
          label="Asset"
          value={filters.metal}
          allLabel="All Assets"
          options={ASSET_SELECT_OPTIONS}
          onChange={(value) => {
            update({ metal: value });
          }}
        />
      )}
    </>
  );
}

function BarTrailing({
  total,
  anyActive,
  clearAll,
  onNewRequest,
}: Readonly<{
  total: number | undefined;
  anyActive: boolean;
  clearAll: () => void;
  onNewRequest: () => void;
}>) {
  return (
    <>
      {total !== undefined && (
        <span className="ml-1 text-xs tabular-nums text-muted-foreground">
          {total.toString()} Request{total === 1 ? '' : 's'}
        </span>
      )}
      {anyActive && (
        <Button variant="ghost" size="sm" onClick={clearAll} className="gap-1">
          <X className="h-4 w-4" aria-hidden />
          Clear filters
        </Button>
      )}
      <div className="flex-1" />
      <Button
        type="button"
        size="sm"
        onClick={onNewRequest}
        data-testid="hedge-book-new-request"
        className="gap-1"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        New Request
      </Button>
    </>
  );
}

const INSTRUMENT_LABEL: Record<HedgeBookInstrument, string> = {
  metals: 'Metals',
  fx: 'FX',
};

function InstrumentToggle({
  value,
  onChange,
}: Readonly<{ value: HedgeBookInstrument; onChange: (value: HedgeBookInstrument) => void }>) {
  return (
    <div className="flex shrink-0 items-center rounded-md border bg-muted/40 p-0.5">
      {INSTRUMENT_OPTIONS.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => {
            onChange(option);
          }}
          aria-pressed={value === option}
          data-testid={`hedge-book-instrument-${option}`}
          className={cn(
            'rounded px-3 py-2 text-xs font-medium transition-colors md:py-1',
            value === option
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {INSTRUMENT_LABEL[option]}
        </button>
      ))}
    </div>
  );
}

interface SearchFieldProps {
  current: string | undefined;
  onSubmit: (value: string | undefined) => void;
}

function SearchField({ current, onSubmit }: Readonly<SearchFieldProps>) {
  const [value, setValue] = useState(current ?? '');
  // Keep the local search input in sync with URL state when filters are
  // cleared externally (e.g. via the "Clear filters" button or back-nav).
  useEffect(() => {
    setValue(current ?? '');
  }, [current]);

  const submit = () => {
    const trimmed = value.trim();
    onSubmit(trimmed.length === 0 ? undefined : trimmed);
  };
  return (
    <div className="relative min-w-[180px] max-w-xs flex-1">
      <Search
        className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        type="search"
        inputMode="search"
        aria-label="Search"
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        onBlur={submit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            submit();
          }
        }}
        placeholder="Search hedges…"
        className="h-9 w-full rounded-md border bg-background pl-8 pr-3 text-base md:text-sm"
      />
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  value: string | undefined;
  allLabel: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string | undefined) => void;
}

function SelectField({ label, value, allLabel, options, onChange }: Readonly<SelectFieldProps>) {
  // Shared Combobox (clearable, placeholder = the "all/active" default) to match
  // the tickets/orders filter bars and the rest of the hedging UI.
  return (
    <Combobox
      aria-label={label}
      options={options}
      value={value}
      onChange={onChange}
      placeholder={allLabel}
      clearable
      searchable={false}
      className="h-9 w-44"
    />
  );
}
